import React, { useEffect, useState, useRef } from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { X } from "lucide-react";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { useCallsStore } from "@/stores/callsStore";
import { SortingState, OnChangeFn } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import CallsTable from "@/components/calls/CallsTable";
import CallsPagination from "@/components/calls/CallsPagination";
import CompactSentenceFilter, { CompactSentenceFilterRef } from "@/components/CompactSentenceFilter";
import SmartSearchActiveBar from "@/components/SmartSearchActiveBar";
import { CallFilterBuilder } from "@/components/CallFilterBuilder";
import { callFilterConfig } from "@/config/callFilterConfig";
import { CallFilterResult, CallQueryResults } from "@/types/callFilters";

const CallsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sentenceFilterRef = useRef<CompactSentenceFilterRef>(null);

  const {
    calls,
    isLoading,
    error,
    fetchCallsAdvanced,
    fetchCallsV3,
    applySmartSearch,
    clearSmartSearch,
    isSmartSearchMode,
    smartSearchQuery,
    smartSearchFilters,
    aggregations,
    currentPage,
    setCurrentPage,
    sortingState,
    setSortingState,
    pageSize,
    setPageSize,
    totalPages,
    totalCalls,
    filters,
    setFilters,
    selectedCalls,
  } = useCallsStore();

  const [activeStructuredFilters, setActiveStructuredFilters] = usePersistedState<CallFilterResult | null>("view:calls:structuredFilters", null);

  const handleApplyStructuredFilters = (filters: CallFilterResult) => {
    setActiveStructuredFilters(filters);
    if (selectedOrganizationId) {
      // Clear smart search if active
      if (isSmartSearchMode) {
        clearSmartSearch();
        sentenceFilterRef.current?.clearQuery();
      }
      fetchCallsV3(selectedOrganizationId, filters);
    }
  };

  const handleClearStructuredFilters = () => {
    setActiveStructuredFilters(null);
    if (selectedOrganizationId) {
      fetchCallsAdvanced(selectedOrganizationId);
    }
  };

  const [sorting, setSorting] = useState<SortingState>(() => {
    // Initialize from URL if available, otherwise use store state
    const urlSortBy = searchParams.get("sort_by");
    const urlSortOrder = searchParams.get("sort_order");
    
    if (urlSortBy) {
      const sortFields = urlSortBy.split(',');
      const sortOrders = (urlSortOrder || 'desc').split(',');
      return sortFields.map((field, index) => ({
        id: field,
        desc: (sortOrders[index] || 'desc') === 'desc'
      }));
    }
    
    return sortingState as SortingState;
  });

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    setSorting(newSorting);
  };

  const { selectedOrganizationId } = useOrganizationsStore();

  // Parse filters from URL on component mount
  const getFiltersFromURL = (): Partial<typeof filters> => {
    const urlFilters: Partial<typeof filters> = {};
    
    const search = searchParams.get("search");
    if (search) urlFilters.search = search;
    
    const callStatus = searchParams.get("call_status");
    if (callStatus) urlFilters.call_status = callStatus;
    
    const strategyType = searchParams.get("strategy_type");
    if (strategyType) urlFilters.strategy_type = strategyType;
    
    const wasSuccessful = searchParams.get("was_successful");
    if (wasSuccessful !== null) urlFilters.was_successful = wasSuccessful === 'true';
    
    const hadVoicemail = searchParams.get("had_voicemail");
    if (hadVoicemail !== null) urlFilters.had_voicemail = hadVoicemail === 'true';
    
    const daysAgo = searchParams.get("days_ago");
    if (daysAgo) urlFilters.days_ago = parseInt(daysAgo);
    
    return urlFilters;
  };

  // Update URL when filters change
  const updateURLParams = (newFilters: typeof filters, newPage: number, newPageSize: number, newSorting: SortingState) => {
    const params = new URLSearchParams();
    
    // Set pagination
    if (newPage > 0) params.set("page", newPage.toString());
    if (newPageSize !== 20) params.set("page_size", newPageSize.toString());
    
    // Set sorting
    if (newSorting.length > 0) {
      const sortBy = newSorting.map(s => s.id).join(',');
      const sortOrder = newSorting.map(s => s.desc ? 'desc' : 'asc').join(',');
      if (sortBy !== 'created_at') params.set("sort_by", sortBy);
      if (sortOrder !== 'desc') params.set("sort_order", sortOrder);
    }
    
    // Set filters
    if (newFilters.search) params.set("search", newFilters.search);
    if (newFilters.call_status) params.set("call_status", newFilters.call_status);
    if (newFilters.strategy_type) params.set("strategy_type", newFilters.strategy_type);
    if (newFilters.was_successful !== null) params.set("was_successful", newFilters.was_successful.toString());
    if (newFilters.had_voicemail !== null) params.set("had_voicemail", newFilters.had_voicemail.toString());
    if (newFilters.days_ago !== null) params.set("days_ago", newFilters.days_ago.toString());
    
    setSearchParams(params, { replace: true });
  };

  // Initialize filters from URL on mount
  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
    }
    
    const urlPage = parseInt(searchParams.get("page") || "0");
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    
    const urlPageSize = parseInt(searchParams.get("page_size") || "20");
    if (urlPageSize !== pageSize) {
      setPageSize(urlPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update store when sorting changes or pagination changes
  useEffect(() => {
    if (selectedOrganizationId) {
      // Choose endpoint based on active filter mode
      if (isSmartSearchMode && smartSearchFilters) {
        fetchCallsV3(selectedOrganizationId, smartSearchFilters);
      } else if (activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0) {
        fetchCallsV3(selectedOrganizationId, activeStructuredFilters);
      } else {
        fetchCallsAdvanced(selectedOrganizationId);
      }
    }
  }, [
    selectedOrganizationId,
    fetchCallsAdvanced,
    fetchCallsV3,
    currentPage,
    pageSize,
    sortingState,
    filters,
    isSmartSearchMode,
    smartSearchFilters,
    activeStructuredFilters,
  ]);

  // Update URL when state changes
  useEffect(() => {
    updateURLParams(filters, currentPage, pageSize, sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, pageSize, sorting]);

  // Update store when sorting changes
  useEffect(() => {
    if (sorting.length > 0) {
      setSortingState(sorting);
    } else {
      setSortingState([]);
    }
  }, [sorting, setSortingState]);


  // Function to handle page size change
  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value));
    setCurrentPage(0); // Reset to first page when changing page size
  };

  // Handle call click with navigation state
  const handleCallClick = (call: any) => {
    const callIndex = calls.findIndex(c => c.id === call.id);
    const callIds = calls.map(c => c.id);
    
    navigate(`/calls/${call.id}`, {
      state: {
        callIds,
        currentIndex: callIndex,
        filters,
        totalCalls,
        returnUrl: `/calls?${searchParams.toString()}`,
        sorting,
        currentPage,
        pageSize
      }
    });
  };

  // Handle sentence filter results
  const handleFiltersApplied = (
    filters: CallFilterResult,
    results: CallQueryResults,
    query: string
  ) => {
    console.log("Smart search filters applied:", filters);
    console.log("Smart search results:", results);
    console.log("Smart search query:", query);
    // Apply smart search to the store
    applySmartSearch(query, filters, results);
  };

  if (error) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        <div className="bg-destructive/15 p-4 rounded-md">
          <p className="text-destructive break-words">Error loading calls: {error}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded"
              onClick={() => fetchCallsAdvanced(selectedOrganizationId as string)}
            >
              Retry
            </button>
            <button
              className="px-4 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded"
              onClick={() => {
                // Clear persisted calls state (store + view filters)
                localStorage.removeItem('calls-storage');
                Object.keys(localStorage)
                  .filter((k) => k.startsWith('view:calls:'))
                  .forEach((k) => localStorage.removeItem(k));
                // Refresh the page
                window.location.reload();
              }}
            >
              Reset & Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full min-w-0 flex-col items-stretch pt-4 md:pt-6">
      <div className="mb-4 px-4 md:px-8">
        <h1 className="text-2xl font-semibold tracking-tight">Calls</h1>
      </div>
      {/* Filters Row */}
      <div className="mb-3 flex flex-col gap-3 px-4 md:flex-row md:items-end md:px-8">
        <div className="min-w-0 flex-1">
          <CompactSentenceFilter
            ref={sentenceFilterRef}
            config={callFilterConfig}
            onFiltersApplied={handleFiltersApplied}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CallFilterBuilder
            onApplyFilters={handleApplyStructuredFilters}
            activeFilters={activeStructuredFilters}
            onClearFilters={handleClearStructuredFilters}
          />
          {(activeStructuredFilters || isSmartSearchMode) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearStructuredFilters}
              className="text-muted-foreground hover:text-destructive whitespace-nowrap"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Summary */}
      {activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0 && (
        <div className="mb-3 px-4 md:px-8">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-muted-foreground font-medium">Active filters:</span>
            {Object.entries(activeStructuredFilters).map(([key, value]) => {
              if (value === null || value === undefined || value === '') return null;
              const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
              return (
                <Badge key={key} variant="secondary" className="text-[11px] font-normal">
                  {key.replace(/_/g, ' ')}: {displayValue}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Smart Search Active Bar */}
      <SmartSearchActiveBar
        isVisible={isSmartSearchMode}
        query={smartSearchQuery}
        filters={smartSearchFilters}
        callAggregations={aggregations}
        onClear={() => {
          clearSmartSearch();
          sentenceFilterRef.current?.clearQuery();
          if (selectedOrganizationId) {
            fetchCallsAdvanced(selectedOrganizationId);
          }
        }}
      />

      {isLoading ? (
        // Loading skeleton
        <div className="space-y-4 flex-1 px-4 md:px-8">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex space-x-4 overflow-hidden">
              <Skeleton className="h-10 w-[200px] shrink-0" />
              <Skeleton className="h-10 w-[100px] shrink-0" />
              <Skeleton className="h-10 w-[150px] shrink-0" />
              <Skeleton className="h-10 w-[100px] shrink-0" />
              <Skeleton className="h-10 w-[100px] shrink-0" />
            </div>
          ))}
        </div>
      ) : (
        <Card className="pt-3 flex-1 flex flex-col min-h-0 min-w-0">
          <CardContent className="flex flex-col gap-2 flex-1 min-h-0 px-3 md:px-6">
            {/* Table */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <CallsTable
                calls={calls}
                sorting={sorting}
                setSorting={handleSortingChange}
                selectedCalls={selectedCalls}
                onCallClick={handleCallClick}
                isSelectEnabled={false} // Can be enabled later for bulk operations
              />
            </div>

            {/* Pagination */}
            <CallsPagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalCalls={totalCalls}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={handlePageSizeChange}
              canPreviousPage={currentPage > 0}
              canNextPage={currentPage < totalPages - 1}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CallsPage;
